/**
 * #panel-collapse mode panel: the docked column and the auto-hide flyout render this.
 */
import { useEffect, useRef, useState } from 'react'
import {
  Activity,
  AlignJustify,
  Archive,
  BookOpen,
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  Heart,
  Home,
  Inbox,
  List,
  Mail,
  Moon,
  MoreVertical,
  PanelLeft,
  Sparkles,
  Workflow,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome'
import { ShellColumnHeader } from '@/components/layout/shell'
import { SHELL_COLUMN_FOOTER_CLASS } from '@/lib/shell-chrome'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import { MODES, type PanelCollapseMode } from './maater-ui-panel-collapse-layout'

function SidebarRow({
  label,
  icon: Icon,
  depth = 0,
  active = false,
  section = false,
  dense = false,
}: {
  label: string
  icon?: typeof Home
  depth?: number
  active?: boolean
  section?: boolean
  dense?: boolean
}) {
  if (section) {
    return (
      <div
        className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide"
        style={{ color: 'var(--ink-faint)' }}
      >
        {label}
      </div>
    )
  }
  return (
    <div
      className={cn(
        'flex items-center gap-1.5 truncate rounded-md pr-2',
        dense ? 'h-6 text-[11px]' : 'h-7 text-xs',
        active && 'bg-primary/15 font-medium text-primary',
      )}
      style={{ paddingLeft: 8 + depth * 12, color: active ? undefined : 'var(--ink)' }}
    >
      {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" /> : <span className="w-3.5 shrink-0" />}
      <span className="truncate">{label}</span>
    </div>
  )
}

function MenuCheck({ on }: { on: boolean }) {
  return (
    <span className="mr-2 flex w-4 shrink-0 items-center justify-center">
      {on ? <Check className="h-3.5 w-3.5" /> : null}
    </span>
  )
}

/** Layout preferences. Same menu on the docked column and the auto-hide flyout. */
function SidebarOptionsMenu({
  autoHide,
  dense,
  openTabsVisible,
  shortcut,
  onAutoHideToggle,
  onDenseChange,
  onOpenTabsVisibleChange,
  onCollapse,
  onOpenChange,
}: {
  autoHide: boolean
  dense: boolean
  openTabsVisible: boolean
  shortcut: string
  onAutoHideToggle: () => void
  onDenseChange: (dense: boolean) => void
  onOpenTabsVisibleChange: (visible: boolean) => void
  onCollapse: () => void
  onOpenChange: (open: boolean) => void
}) {
  const openRef = useRef(false)
  const onOpenChangeRef = useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange
  // Picking Auto-hide unmounts the docked column with its menu open; report it closed.
  useEffect(
    () => () => {
      if (openRef.current) onOpenChangeRef.current(false)
    },
    [],
  )
  return (
    <DropdownMenu
      onOpenChange={(open) => {
        openRef.current = open
        onOpenChange(open)
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Sidebar options"
          title="Sidebar options"
        >
          <MoreVertical className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
        data-testid={testIds.maaterUiGallery.panelCollapseMenu}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <DropdownMenuItem onSelect={() => onDenseChange(false)}>
          <AlignJustify className="mr-2 h-3.5 w-3.5" />
          Detailed layout
          {!dense ? <Check className="ml-auto h-3.5 w-3.5" /> : null}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onDenseChange(true)}>
          <List className="mr-2 h-3.5 w-3.5" />
          Dense layout
          {dense ? <Check className="ml-auto h-3.5 w-3.5" /> : null}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onOpenTabsVisibleChange(!openTabsVisible)}>
          <MenuCheck on={openTabsVisible} />
          {openTabsVisible ? 'Hide Open Tabs' : 'Show Open Tabs'}
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={testIds.maaterUiGallery.panelCollapseAutoHide}
          onSelect={onAutoHideToggle}
        >
          <MenuCheck on={autoHide} />
          Auto-hide sidebar
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onCollapse}>
          <PanelLeft className="mr-2 h-3.5 w-3.5" />
          Collapse sidebar
          <span className="ml-auto pl-3 text-[10px] text-muted-foreground">{shortcut}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** The docked column and the hover flyout. Rail clicks pick which one. Hover does not. */
export function ActiveSidebar({
  mode,
  enterClass,
  enterKey,
  autoHide,
  dense,
  openTabsVisible,
  shortcut,
  onAutoHideToggle,
  onDenseChange,
  onOpenTabsVisibleChange,
  onCollapse,
  onMenuOpenChange,
}: {
  mode: PanelCollapseMode
  enterClass: string
  enterKey: number
  autoHide: boolean
  dense: boolean
  openTabsVisible: boolean
  shortcut: string
  onAutoHideToggle: () => void
  onDenseChange: (dense: boolean) => void
  onOpenTabsVisibleChange: (visible: boolean) => void
  onCollapse: () => void
  onMenuOpenChange: (open: boolean) => void
}) {
  const title = MODES.find((item) => item.id === mode)?.label
  const row = { dense }
  return (
    <div
      key={enterKey}
      className={cn('flex min-h-0 min-w-0 flex-1 flex-col', enterKey > 0 && enterClass)}
      data-sidebar-density={dense ? 'dense' : 'detailed'}
    >
      <ShellColumnHeader
        title={title}
        optionsMenu={
          <SidebarOptionsMenu
            autoHide={autoHide}
            dense={dense}
            openTabsVisible={openTabsVisible}
            shortcut={shortcut}
            onAutoHideToggle={onAutoHideToggle}
            onDenseChange={onDenseChange}
            onOpenTabsVisibleChange={onOpenTabsVisibleChange}
            onCollapse={onCollapse}
            onOpenChange={onMenuOpenChange}
          />
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto py-1">
        {mode === 'browse' && (
          <>
            <SidebarRow label="Home" icon={Home} active {...row} />
            <SidebarRow label="Workspaces" section />
            <SidebarRow label="Hop Studios" icon={ChevronDown} {...row} />
            <SidebarRow label="My documents" icon={Folder} depth={1} {...row} />
            <SidebarRow label="Pinned" icon={ChevronDown} depth={1} {...row} />
            <SidebarRow label="HOP - DAILY SCRUM" icon={FileText} depth={2} {...row} />
            <SidebarRow label="Weekly Priorities & Notes" icon={FileText} depth={2} {...row} />
            <SidebarRow label="Documents" icon={ChevronDown} depth={1} {...row} />
            <SidebarRow label=".skills" icon={Folder} depth={2} {...row} />
            <SidebarRow label=".soul" icon={Folder} depth={2} {...row} />
            <SidebarRow label="agents.md" icon={Heart} depth={3} {...row} />
          </>
        )}
        {mode === 'inbox' && (
          <>
            <SidebarRow label="Inbox" icon={Inbox} active {...row} />
            <SidebarRow label="Flows" icon={Workflow} {...row} />
            <SidebarRow label="Chats" icon={Bot} {...row} />
            <SidebarRow label="Suggestions" icon={Sparkles} {...row} />
            <SidebarRow label="Email" icon={Mail} {...row} />
            <SidebarRow label="Snoozed" icon={Moon} {...row} />
            <SidebarRow label="Archived" icon={Archive} {...row} />
          </>
        )}
        {mode === 'flows' && (
          <>
            <SidebarRow label="Pulse" icon={Activity} active {...row} />
            <SidebarRow label="Gallery" icon={BookOpen} {...row} />
            <SidebarRow label="Sources" icon={Folder} {...row} />
            <SidebarRow label="Composer" icon={Sparkles} {...row} />
          </>
        )}
        {mode === 'chats' && (
          <>
            <SidebarRow label="Today" section />
            <SidebarRow label="Weekly todo triage" icon={Bot} active {...row} />
            <SidebarRow label="Estimate for the hub" icon={Bot} {...row} />
            <SidebarRow label="Earlier" section />
            <SidebarRow label="Sidebar tree continuity" icon={Bot} {...row} />
          </>
        )}
      </div>
      {openTabsVisible ? <OpenTabsDrawer dense={dense} /> : null}
    </div>
  )
}

const OPEN_TABS = [
  { label: 'Inbox', icon: Inbox },
  { label: 'Documents', icon: Folder },
  { label: 'Late-todo shadow', icon: FileText },
]

/**
 * Open Tabs at the bottom of the mode column (proposed revision of unified-sidebar
 * Decision #8). Collapsed, its header is the column footer and lines up with the rail
 * Settings row and the canvas status bar. Expanded, the rows grow upward over the lens.
 */
function OpenTabsDrawer({ dense }: { dense: boolean }) {
  const [expanded, setExpanded] = useState(true)
  const Chevron = expanded ? ChevronDown : ChevronRight
  return (
    <div
      className="flex shrink-0 flex-col border-t"
      style={{ borderColor: 'var(--line)' }}
      data-testid={testIds.maaterUiGallery.panelCollapseOpenTabs}
    >
      {expanded ? (
        <div className="max-h-40 overflow-y-auto py-1">
          {OPEN_TABS.map((tab) => (
            <SidebarRow key={tab.label} label={tab.label} icon={tab.icon} dense={dense} />
          ))}
        </div>
      ) : null}
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
        className={cn(
          SHELL_COLUMN_FOOTER_CLASS,
          'flex w-full items-center gap-1.5 px-3 text-left',
          expanded && 'border-t',
        )}
        style={{ borderColor: 'var(--line)' }}
      >
        <Chevron className="h-3 w-3 shrink-0" style={{ color: 'var(--ink-faint)' }} />
        <span
          className="text-[11px] font-bold uppercase tracking-widest"
          style={{ color: 'var(--ink-soft)' }}
        >
          Open Tabs
        </span>
        <span className="text-[11px] tabular-nums" style={{ color: 'var(--ink-faint)' }}>
          ({OPEN_TABS.length})
        </span>
      </button>
    </div>
  )
}
